import { useEffect, useRef, useState } from 'react';
import { Panel } from './Panel';
import { calculate, CalculatorError, type CalcOutcome } from '@/domain/calculator';
import { CALC_HISTORY_KEPT } from '@/constants/app';

/**
 * The scientific calculator, as a panel so it can be open over a question.
 *
 * It stays available during exams on purpose: his real exams allow a
 * calculator. The Library is the thing that closes.
 *
 * What is typed is evaluated by the app's own expression parser — the same one
 * the problem templates use — so there is no `eval` here, and no way for an
 * expression to reach anything outside that file.
 */

interface Entry {
  expression: string;
  result: string;
}

/**
 * Kept outside the component so closing the panel and opening it again does
 * not lose the working or the previous answer. Memory, not storage: a reload
 * starts clean, which is what a calculator does.
 */
const memory: { expression: string; history: Entry[]; ans: number | undefined } = {
  expression: '',
  history: [],
  ans: undefined,
};

interface Key {
  label: string;
  insert: string;
  op?: boolean;
}

const KEYS: Key[] = [
  { label: '(', insert: '(' },
  { label: ')', insert: ')' },
  { label: '^', insert: '^', op: true },
  { label: '÷', insert: '/', op: true },
  { label: '7', insert: '7' },
  { label: '8', insert: '8' },
  { label: '9', insert: '9' },
  { label: '×', insert: '*', op: true },
  { label: '4', insert: '4' },
  { label: '5', insert: '5' },
  { label: '6', insert: '6' },
  { label: '−', insert: '-', op: true },
  { label: '1', insert: '1' },
  { label: '2', insert: '2' },
  { label: '3', insert: '3' },
  { label: '+', insert: '+', op: true },
  { label: '0', insert: '0' },
  { label: '.', insert: '.' },
  { label: 'e', insert: 'e' },
  { label: 'ans', insert: 'ans', op: true },
];

const FUNCTIONS: Key[] = [
  { label: '√', insert: 'sqrt(' },
  { label: 'x²', insert: '^2' },
  { label: 'log', insert: 'log10(' },
  { label: 'ln', insert: 'ln(' },
  { label: 'exp', insert: 'exp(' },
  { label: 'π', insert: 'pi' },
  { label: '|x|', insert: 'abs(' },
  { label: 'molar mass', insert: 'molarMass(' },
];

export function CalculatorPanel({ onClose }: { onClose: () => void }) {
  const [expression, setExpression] = useState(memory.expression);
  const [history, setHistory] = useState<Entry[]>(memory.history);
  const [outcome, setOutcome] = useState<CalcOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    memory.expression = expression;
  }, [expression]);

  useEffect(() => {
    memory.history = history;
  }, [history]);

  function insert(text: string) {
    setExpression((current) => current + text);
    setError(null);
    field.current?.focus();
  }

  function equals() {
    try {
      const next = calculate(expression, { ans: memory.ans });
      memory.ans = next.value;
      setOutcome(next);
      setError(null);
      setHistory((h) =>
        [{ expression: expression.trim(), result: next.display }, ...h].slice(0, CALC_HISTORY_KEPT),
      );
    } catch (err) {
      setOutcome(null);
      setError(err instanceof CalculatorError ? err.message : 'That did not work out.');
    }
  }

  return (
    <Panel title="Calculator" onClose={onClose}>
      <div className="stack">
        <label className="label" htmlFor="calc-expression">
          Working
        </label>
        <input
          id="calc-expression"
          ref={field}
          className="input calc__field"
          value={expression}
          spellCheck={false}
          autoComplete="off"
          placeholder="5.00 / molarMass(NaCl)"
          onChange={(e) => {
            setExpression(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              equals();
            }
          }}
        />

        {error ? <div className="banner banner--error">{error}</div> : null}

        {outcome ? (
          <div className="card stack">
            <div className="calc__value">{outcome.display}</div>
            {outcome.sigfigDisplay && outcome.sigfigs !== null ? (
              <div className="faint">
                {outcome.sigfigDisplay} to {outcome.sigfigs} significant figure
                {outcome.sigfigs === 1 ? '' : 's'}
                {outcome.ambiguous
                  ? ' — though a whole number written without a decimal point, like 100, does not say how many figures it carries'
                  : ''}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="calc__functions">
          {FUNCTIONS.map((fn) => (
            <button key={fn.label} className="btn btn--sm" onClick={() => insert(fn.insert)}>
              {fn.label}
            </button>
          ))}
        </div>

        <div className="calc__pad">
          {KEYS.map((pad) => (
            <button
              key={pad.label}
              className={`calc__key${pad.op ? ' calc__key--op' : ''}`}
              onClick={() => insert(pad.insert)}
            >
              {pad.label}
            </button>
          ))}
        </div>

        <div className="row">
          <button
            className="btn"
            onClick={() => {
              setExpression((current) => current.slice(0, -1));
              setError(null);
            }}
          >
            ⌫ Back
          </button>
          <button
            className="btn"
            onClick={() => {
              setExpression('');
              setOutcome(null);
              setError(null);
            }}
          >
            Clear
          </button>
          <button className="btn btn--primary" onClick={equals} disabled={!expression.trim()}>
            =
          </button>
        </div>

        <p className="faint" style={{ margin: 0 }}>
          Brackets left open are closed for you, and <span className="mono">ans</span> is the
          previous answer. <span className="mono">molarMass</span> takes a formula —
          molarMass(Ca(OH)2) — and uses the masses printed on your own periodic table. No
          trigonometry: this course does not use any.
        </p>

        {history.length > 0 ? (
          <>
            <h3 className="section-title">Earlier</h3>
            <ul className="list calc__history">
              {history.map((entry, i) => (
                <li key={`${entry.expression}-${i}`}>
                  <button
                    className="calc__recall"
                    onClick={() => {
                      setExpression(entry.expression);
                      field.current?.focus();
                    }}
                  >
                    <span className="mono faint">{entry.expression}</span>
                    <span className="mono">= {entry.result}</span>
                  </button>
                </li>
              ))}
            </ul>
            <button
              className="btn btn--ghost"
              onClick={() => {
                setHistory([]);
                memory.ans = undefined;
              }}
            >
              Clear history
            </button>
          </>
        ) : null}
      </div>
    </Panel>
  );
}
